import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Button,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import { ArrowBack, Edit, Home, Menu as MenuIcon, Logout } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getBackRoute, getHomeRoute } from '../utils/navigationUtils';

interface AppHeaderProps {
  title: string;
  subtitle?: string;
  showBackButton?: boolean;
  backTo?: string;
  icon?: React.ReactNode;
}

export const AppHeader: React.FC<AppHeaderProps> = ({
  title,
  subtitle,
  showBackButton = true,
  backTo,
  icon
}) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  // Use backTo fornecido ou determine baseado no role do usuário
  const defaultBackTo = backTo || getBackRoute(user?.role);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    window.location.href = '/login';
  };

  const handleBack = () => {
    if (user?.role === 'SUPER_ADMIN' && defaultBackTo.startsWith('/admin')) {
      window.location.href = defaultBackTo;
    } else {
      navigate(defaultBackTo);
    }
  };

  const handleProfile = () => {
    handleMenuClose();
    if (user?.role === 'SUPER_ADMIN') {
      window.location.href = '/profile';
    } else {
      navigate('/profile');
    }
  };

  const handleHome = () => {
    const homeRoute = getHomeRoute(user?.role);
    if (user?.role === 'SUPER_ADMIN' && homeRoute.startsWith('/admin')) {
      window.location.href = homeRoute;
    } else {
      navigate(homeRoute);
    }
  };

  return (
    <AppBar 
      position="static" 
      elevation={0}
      sx={{ 
        background: 'linear-gradient(135deg, #009929 0%, #1565c0 100%)',
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}
    >
      <Toolbar sx={{ minHeight: '64px !important' }}>
        {showBackButton && (
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleBack}
            sx={{ 
              mr: 2,
              backgroundColor: 'rgba(255,255,255,0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.2)',
              }
            }}
          >
            <ArrowBack />
          </IconButton>
        )}
        
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          {icon && (
            <Box sx={{ mr: 2, fontSize: '1.5rem' }}>
              {icon}
            </Box>
          )}
          <Box>
            <Typography 
              variant="h6" 
              component="div" 
              sx={{ 
                fontWeight: 600,
                fontSize: '1.25rem',
                lineHeight: 1.2,
                color: 'white'
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography 
                variant="body2" 
                sx={{ 
                  opacity: 0.9,
                  fontSize: '0.875rem',
                  fontWeight: 400,
                  color: 'white'
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {user && (
            <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
                Olá, {user.name}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.75rem' }}>
                {user.role === 'ASSOCIACAO' ? 'Associação' : 
                 user.role === 'SUPER_ADMIN' ? 'Super Admin' : 'Usuário'}
              </Typography>
            </Box>
          )}
          <IconButton
            color="inherit"
            onClick={handleHome}
            sx={{ 
              backgroundColor: 'rgba(255,255,255,0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.2)',
              }
            }}
            title="Ir para o Dashboard"
          >
            <Home />
          </IconButton>
          
          {/* Botões para Desktop */}
          <Button 
            color="inherit" 
            onClick={handleProfile}
            startIcon={<Edit />}
            sx={{ 
              fontWeight: 600,
              backgroundColor: 'rgba(255,255,255,0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.2)',
              },
              borderRadius: 2,
              px: 2,
              display: { xs: 'none', md: 'flex' }
            }}
          >
            Editar Perfil
          </Button>
          <Button 
            color="inherit" 
            onClick={handleLogout} 
            sx={{ 
              fontWeight: 600,
              backgroundColor: 'rgba(255,255,255,0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.2)',
              },
              borderRadius: 2,
              px: 2,
              display: { xs: 'none', md: 'flex' }
            }}
          >
            Sair
          </Button>

          {/* Menu Hambúrguer para Mobile */}
          <IconButton
            color="inherit"
            onClick={handleMenuOpen}
            sx={{ 
              backgroundColor: 'rgba(255,255,255,0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.2)',
              },
              display: { xs: 'flex', md: 'none' }
            }}
            title="Menu"
          >
            <MenuIcon />
          </IconButton>

          {/* Menu Dropdown */}
          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              elevation: 3,
              sx: {
                mt: 1.5,
                minWidth: 200,
                borderRadius: 2,
              }
            }}
          >
            {user && [
              <Box key="user-info" sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {user.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user.role === 'ASSOCIACAO' ? 'Associação' : 
                   user.role === 'SUPER_ADMIN' ? 'Super Admin' : 'Usuário'}
                </Typography>
              </Box>,
              <Divider key="divider-1" />
            ]}
            
            <MenuItem onClick={handleProfile}>
              <ListItemIcon>
                <Edit fontSize="small" />
              </ListItemIcon>
              <ListItemText>Editar Perfil</ListItemText>
            </MenuItem>
            
            <Divider />
            
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText>Sair</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}; 